import {
  HTTPForbiddenError,
  HTTPNotFoundError,
  HTTPRequestError,
  HTTPResponseError,
  HTTPUnauthorizedError,
  HTTPUtils,
  type BaseErrorSubClassInstanceProps,
  type HTTPResponseErrorProps,
} from "@validereinc/utilities";
import { type RequestWrapperType, requestWrapper } from "./requestWrapper.ts";
import { type RequestCallbackType } from "./types.ts";

export type PUTRequestType = {
  version?: number;
} & Pick<
  RequestWrapperType,
  "endpoint" | "body" | "headers" | "query" | "responseType"
>;

type PUTProps = {
  api: string;
  requestCallback?: RequestCallbackType;
} & PUTRequestType;

/**
 * Make an HTTP PUT request. Default response parsing is json. If you need the
 * response object directly, use fetch() instead.
 *
 * @param props - PUT request props
 * @returns response body as T
 */
export const PUT = async <T = unknown>({
  api,
  endpoint,
  body,
  query,
  headers = {},
  responseType = "json",
  requestCallback = (response) => response,
}: PUTProps): Promise<T> => {
  try {
    const request = requestWrapper({
      method: "PUT",
      endpoint: `${api}${endpoint}`,
      query,
      body,
      headers,
    });
    const response = requestCallback
      ? await fetch(request).then(requestCallback)
      : await fetch(request);

    if (HTTPUtils.status.isSuccess(response.status)) {
      switch (responseType) {
        case "arraybuffer":
          return (await response.arrayBuffer()) as T;
        case "blob":
          return (await response.blob()) as T;
        case "json": {
          return (await response.json()) as T;
        }
        default:
          return {} as T;
      }
    } else {
      throw { response, request };
    }
  } catch (cause: unknown) {
    if (!HTTPUtils.objectHasResponseAndRequest(cause)) {
      throw new HTTPRequestError(
        "PUT fetch failure or request utility error:",
        {
          method: "PUT",
          endpoint: `${api}${endpoint}`,
          requestQuery: query,
          requestContentType: headers["Content-Type"],
          requestBodyJson: body,
          cause,
        }
      );
    }

    // cause excluded since it is a custom object we constructed
    const props: BaseErrorSubClassInstanceProps<HTTPResponseErrorProps> = {
      method: "PUT",
      status: cause.response.status,
      endpoint: cause.request.url,
      requestQuery: query,
      requestContentType: headers["Content-Type"],
      requestBodyJson: body,
      responseContentType: cause.response.headers.get("Content-Type"),
    };

    if (
      cause.response.headers.get("Content-Type")?.includes("application/json")
    ) {
      if (HTTPUtils.status.isNotAuthorized(cause.response.status)) {
        throw new HTTPUnauthorizedError("PUT unauthorized", {
          ...props,
          responseBodyJson: await cause.response.json(),
        });
      } else if (HTTPUtils.status.isForbidden(cause.response.status)) {
        throw new HTTPForbiddenError("PUT forbidden", {
          ...props,
          responseBodyJson: await cause.response.json(),
        });
      } else if (HTTPUtils.status.isNotFound(cause.response.status)) {
        throw new HTTPNotFoundError("PUT not found", {
          ...props,
          responseBodyJson: await cause.response.json(),
        });
      } else {
        throw new HTTPResponseError("PUT error with info from API", {
          ...props,
          responseBodyJson: await cause.response.json(),
        });
      }
    }

    switch (cause.response.status) {
      case 401:
        throw new HTTPUnauthorizedError("PUT unauthorized", props);
      case 403:
        throw new HTTPForbiddenError("PUT forbidden", props);
      case 404:
        throw new HTTPNotFoundError("PUT not found", props);
      default:
        throw new HTTPResponseError("PUT unknown error", props);
    }
  }
};
